import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { getLenis } from "./LenisProvider";

/**
 * FloatingNavbar — sticky anchor menu (inline + sticks on scroll).
 *
 * Behaviour (revised 2026-05-19 after field feedback):
 *   • Lives **inline** in the document flow, rendered wherever the
 *     parent places it. No fixed positioning, no "appears after
 *     scrolling past hero" gating — the menu is visible from the very
 *     top so users can jump directly to any section (e.g. Etapa 5)
 *     without scrolling first.
 *   • Position: sticky with `top` computed from the AppHeader +
 *     action-bar + beta-banner height (CSS vars). When the user
 *     scrolls past it, the menu sticks to the top edge below the
 *     header and stays visible while reading content.
 *   • Active item is auto-detected via IntersectionObserver and gets
 *     the orange pill background + shimmer pulse.
 *   • Smooth-scrolls to the target on click via Lenis (with offset
 *     to clear the header). Falls back to native window.scrollTo
 *     when Lenis isn't available.
 *
 * Works for ANY number of items (1, 3, 10, …). On mobile the pill
 * scrolls horizontally inside its container (scrollbar-hide) so it
 * never gets clipped.
 *
 * Props:
 *   items: Array<{ id: string, label: string, icon?: React.ComponentType }>
 *   scrollOffset?: number       — additional offset (px) when anchoring.
 *                                  Default -100 to clear the AppHeader.
 *   testId?: string
 */
export function FloatingNavbar({
  items,
  scrollOffset,
  testId = "trip-floating-navbar",
  alignToSelector = "[data-testid=\"trip-action-circles\"]",
}) {
  const [activeId, setActiveId] = useState(items?.[0]?.id || null);
  const navRef = useRef(null);
  const [topPx, setTopPx] = useState(null);
  const navBottomRef = useRef(160);
  // Lock active id for ~1.4s after user clicks a nav item, so the smooth
  // scroll can settle without the scroll-spy overriding the chosen section
  // when the page is too short to scroll the section fully to the top.
  const lockUntilRef = useRef(0);
  const itemsKey = (items || []).map((i) => i.id).join("|");

  // b99.9.100 — Dynamic vertical alignment: position the navbar so its BOTTOM
  // aligns with the bottom of the action circles row in the title block.
  // We keep this position fixed in the viewport (relative to its initial
  // document Y), so as the user scrolls the navbar stays put at the same
  // viewport Y where the circles initially were. Falls back to the header
  // offset when the target element is missing. Also exposes navBottomY for
  // scroll/active-section calculations so sections never end up hidden
  // behind the floating navbar.
  useLayoutEffect(() => {
    const compute = () => {
      const target = alignToSelector ? document.querySelector(alignToSelector) : null;
      const nav = navRef.current;
      if (!nav) return;
      if (target) {
        const navHeight = nav.offsetHeight || 44;
        const rectBottom = target.getBoundingClientRect().bottom;
        const newTop = Math.max(0, rectBottom - navHeight);
        if (window.scrollY < 100) {
          setTopPx(newTop);
          navBottomRef.current = newTop + navHeight;
        }
      } else {
        setTopPx(null);
      }
    };
    compute();
    window.addEventListener("resize", compute);
    const t1 = setTimeout(compute, 200);
    const t2 = setTimeout(compute, 800);
    return () => {
      window.removeEventListener("resize", compute);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [alignToSelector, itemsKey]);

  // Active section detection — uses scroll position relative to each section's
  // top vs the navbar's BOTTOM (the activation line). A section is "active"
  // when its top has scrolled to or above the navbar's bottom edge — i.e.,
  // when the section is the first visible content below the floating navbar.
  useEffect(() => {
    if (!items?.length) return;
    const computeActive = () => {
      // While a click-driven smooth scroll is settling, keep the active id
      // exactly as the user requested — even if the page can't physically
      // scroll the section all the way under the navbar.
      if (Date.now() < lockUntilRef.current) return;
      const activationY = navBottomRef.current + 16;
      let bestId = items[0]?.id;
      for (const it of items) {
        const el = document.getElementById(it.id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= activationY) {
          bestId = it.id;
        }
      }
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 24;
      if (atBottom) bestId = items[items.length - 1]?.id || bestId;
      setActiveId(bestId);
    };
    computeActive();
    window.addEventListener("scroll", computeActive, { passive: true });
    window.addEventListener("resize", computeActive);
    return () => {
      window.removeEventListener("scroll", computeActive);
      window.removeEventListener("resize", computeActive);
    };
  }, [itemsKey]);

  const handleClick = (id) => (e) => {
    e.preventDefault();
    const target = document.getElementById(id);
    if (!target) return;
    // b99.9.100 — measure CURRENT navbar bottom directly at click time.
    const nav = navRef.current;
    const navBottomY = nav ? nav.getBoundingClientRect().bottom : navBottomRef.current;
    const offset = scrollOffset != null ? scrollOffset : -(navBottomY + 8);
    const targetDocY = target.getBoundingClientRect().top + window.scrollY + offset;
    const lenis = getLenis();
    if (lenis) {
      // b99.9.100 — Pass numeric scroll target (computed from
      // getBoundingClientRect) so Lenis doesn't rely on element.offsetTop,
      // which can be wrong for elements nested inside `display:contents`
      // wrappers with order-* reshuffling.
      lenis.scrollTo(targetDocY, { duration: 1.2 });
    } else {
      // b99.9.100 — Some smooth-scroll handlers in the page stop the scroll
      // early; use instant scroll to guarantee the section reaches the
      // intended position, then let the user see it land.
      window.scrollTo({ top: targetDocY, behavior: "smooth" });
      // Backstop: if smooth scroll didn't reach target after ~600ms,
      // force the final position so the section is correctly aligned.
      setTimeout(() => {
        if (Math.abs(window.scrollY - targetDocY) > 4) {
          window.scrollTo({ top: targetDocY, behavior: "auto" });
        }
      }, 800);
    }
    setActiveId(id);
    lockUntilRef.current = Date.now() + 1400;
  };

  if (!items?.length) return null;

  return (
    <nav
      ref={navRef}
      // b99.9.100 — fixed: posição vertical determinada dinamicamente para
      // alinhar o BOTTOM da navbar com o BOTTOM dos círculos de ação. Quando
      // a medição falha, faz fallback ao offset clássico (header + 1.5rem).
      style={{
        top: topPx != null ? `${topPx}px` : "calc(var(--lang-banner-h, 0px) + var(--beta-banner-h, 0px) + var(--mototrips-header-bottom) + 1.5rem)",
      }}
      className="hidden md:block fixed left-0 right-0 z-[2000] pointer-events-none"
      data-testid={testId}
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
      <div className="pointer-events-auto flex items-center gap-1 rounded-full border border-white/15 bg-black/55 backdrop-blur-xl pl-2 pr-2 py-1.5 shadow-[0_8px_30px_rgba(0,0,0,0.5)] overflow-x-auto max-w-full scrollbar-hide">
        {items.map((it) => {
          const Icon = it.icon;
          const isActive = activeId === it.id;
          // Compact mode on small screens: hide the text label,
          // show only the icon. For etapa-N items we surface the
          // number as a tiny badge next to the icon so the active
          // section is still readable at a glance.
          const etapaMatch = it.id?.match(/^etapa-(\d+)$/);
          const etapaNumber = etapaMatch ? etapaMatch[1] : null;
          return (
            <a
              key={it.id}
              href={`#${it.id}`}
              onClick={handleClick(it.id)}
              className={`relative inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${
                isActive
                  ? "text-white"
                  : "text-white/70 hover:text-white"
              }`}
              data-testid={`${testId}-item-${it.id}`}
              aria-current={isActive ? "true" : undefined}
              aria-label={it.label}
            >
              {isActive && (
                <motion.span
                  layoutId={`${testId}-active-pill`}
                  className="absolute inset-0 rounded-full bg-primary/85 shadow-[0_0_18px_rgba(249,115,22,0.45)]"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              {/* Shimmer line on active pill — slides left→right once
                   when this item becomes active, giving the menu a
                   subtle highlight pulse as the user scrolls into
                   each section. */}
              {isActive && (
                <motion.span
                  key={`shimmer-${it.id}`}
                  initial={{ x: "-100%", opacity: 0 }}
                  animate={{ x: "120%", opacity: [0, 0.7, 0] }}
                  transition={{ duration: 0.9, ease: "easeOut" }}
                  className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-gradient-to-r from-transparent via-white/35 to-transparent pointer-events-none"
                />
              )}
              {Icon && <Icon className="relative h-3.5 w-3.5 md:h-4 md:w-4" />}
              {/* On mobile (<sm), labels are hidden to keep the pill
                   compact even with 5+ items. The etapa number is
                   overlaid on the icon as a badge so users still
                   know which segment they're on. */}
              {etapaNumber && (
                <span className="relative sm:hidden text-[10px] font-bold leading-none">{etapaNumber}</span>
              )}
              <span className="relative hidden sm:inline">{it.label}</span>
            </a>
          );
        })}
      </div>
      </div>
    </nav>
  );
}
